import React, { useState, useEffect, useCallback } from 'react';
import { Heart, MapPin, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from '@/contexts/LocationContext';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import PlaceCard from '@/components/PlaceCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const FavoritesPage = () => {
  const { t } = useLanguage();
  const { location } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadFavorites = useCallback(async () => {
    try {
      const res = await api.getFavorites(location?.lat, location?.lng);
      setFavorites(res.data);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    if (user) {
      loadFavorites();
    } else {
      setLoading(false);
    }
  }, [user, loadFavorites]);

  const handleRemoveFavorite = async (placeId) => {
    try {
      await api.removeFavorite(placeId);
      setFavorites(prev => prev.filter(p => p.id !== placeId));
      toast.success('Eliminado de favoritos');
    } catch (error) {
      toast.error('Error al eliminar favorito');
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4" data-testid="favorites-login-prompt">
        <div className="w-20 h-20 bg-[#E29578]/20 rounded-full flex items-center justify-center mb-6">
          <Heart size={40} className="text-[#E29578]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1D1D1F] mb-2 text-center">
          Inicia sesión para ver tus favoritos
        </h2>
        <p className="text-gray-500 text-center mb-6 max-w-md">
          Guarda tus lugares favoritos para encontrarlos fácilmente y planificar tu viaje
        </p>
        <Button
          onClick={() => navigate('/auth')}
          className="bg-[#38B2AC] hover:bg-[#005A63] rounded-full px-8"
          data-testid="login-btn-favorites"
        >
          {t('login')}
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-48 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-80" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#E29578]/20 rounded-xl flex items-center justify-center">
            <Heart className="text-[#E29578]" size={24} />
          </div>
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1D1D1F]">
              {t('favorites')}
            </h1>
            <p className="text-gray-500">{favorites.length} lugares guardados</p>
          </div>
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart size={40} className="text-gray-300" />
            </div>
            <h3 className="text-xl font-semibold text-[#1D1D1F] mb-2">
              Aún no tienes favoritos
            </h3>
            <p className="text-gray-500 mb-6">
              Explora los lugares y guarda tus favoritos tocando el corazón
            </p>
            <Button
              onClick={() => navigate('/explore')}
              className="bg-[#38B2AC] hover:bg-[#005A63] rounded-full"
            >
              Explorar lugares
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {favorites.map((place) => (
              <PlaceCard
                key={place.id}
                place={place}
                onFavoriteClick={handleRemoveFavorite}
                isFavorite={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
